import gdprBadge from '@/assets/compliance/GDPR.png';
import hipaaBadge from '@/assets/compliance/HIPAA.jpeg';
import phipaPipedaBadge from '@/assets/compliance/PHIPA_PIPEDA.png';

/**
 * Compliance badges (HIPAA / PHIPA-PIPEDA / GDPR) linking to our trust
 * center. Single reusable component so if product later wants the badges
 * on auth pages, the dashboard footer, or PDFs, the styling stays in one
 * place.
 *
 * - Each badge opens trust.chartai.ai in a new tab with
 *   rel="noopener noreferrer".
 * - Fixed height + object-contain so the mixed aspect ratios (HIPAA jpeg
 *   vs square pngs) align on a common baseline.
 * - White pill background neutralizes the HIPAA jpeg's lack of
 *   transparency when placed on dark surfaces.
 * - `size="sm"` is tuned for the 48px TopBar; `size="md"` is for
 *   footers / larger surfaces.
 */
interface ComplianceBadgesProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  noLink?: boolean;
}

const BADGES = [
  {
    src: hipaaBadge,
    alt: 'HIPAA Compliant',
    title: 'HIPAA Compliant — view our trust center',
  },
  {
    src: phipaPipedaBadge,
    alt: 'PHIPA and PIPEDA Compliant',
    title: 'PHIPA / PIPEDA Compliant — view our trust center',
  },
  {
    src: gdprBadge,
    alt: 'GDPR Compliant',
    title: 'GDPR Compliant — view our trust center',
  },
] as const;

export function ComplianceBadges({
  size = 'md',
  className = '',
  noLink = false,
}: ComplianceBadgesProps) {
  const imgHeight = size === 'sm' ? 'h-7' : size === 'md' ? 'h-11' : 'h-14';
  const imgMax = size === 'sm' ? 'max-w-[72px]' : size === 'md' ? 'max-w-[110px]' : 'max-w-[140px]';
  const gap = size === 'sm' ? 'gap-3' : size === 'md' ? 'gap-4' : 'gap-5';

  const images = BADGES.map((badge) => (
    <img
      key={badge.alt}
      src={badge.src}
      alt={badge.alt}
      title={badge.title}
      loading="lazy"
      className={`${imgHeight} w-auto ${imgMax} object-contain`}
    />
  ));

  if (noLink) {
    return (
      <div className={`flex flex-wrap items-center justify-center ${gap} ${className}`}>
        {images}
      </div>
    );
  }

  return (
    <a
      href="https://trust.chartai.ai/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="View our compliance certifications — HIPAA, PHIPA/PIPEDA, GDPR"
      className={`inline-flex flex-wrap items-center justify-center ${gap} ${className} focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none`}
    >
      {images}
    </a>
  );
}
